import type { LoggerOptions } from 'types/Client';

import { isLoggingEnabled } from 'utils/loggingUtils';
import { isStorybook } from 'utils/storybookUtils';

import type { User } from 'store/client/graphql';

import DatadogLogger from './DatadogLogger';
import { trackEvent } from './GTMLogger';

export { GTMEvents } from './GTMLogger';

// eslint-disable-next-line unicorn/no-static-only-class
class LoggingService {
  static captureException(error: Error, info: Record<string, any>) {
    if (!LoggingService.isEnabled()) {
      return;
    }

    DatadogLogger.captureException(error, info);
  }

  static captureReactError(error: Error, info: Record<string, any>) {
    if (!LoggingService.isEnabled()) {
      return;
    }

    DatadogLogger.captureReactError(error, info);
  }

  static clearUser(): void {
    if (!LoggingService.isEnabled()) {
      return;
    }

    DatadogLogger.clearUser();
  }

  static debug(options: LoggerOptions) {
    const { message } = options;
    console.debug(message, options);
  }

  static error(options: Pick<LoggerOptions, 'error' | 'message' | 'messageContext'>) {
    const { error, message, messageContext } = options;
    console.error(message, options);

    if (!LoggingService.isEnabled()) {
      return;
    }
    DatadogLogger.log({ error, message, messageContext, status: 'error' });
  }

  static init(): void {
    if (!LoggingService.isEnabled()) {
      return;
    }

    DatadogLogger.init(this);
  }

  /**
   * Checks if LoggingService is enabled.
   *
   * Cypress CI is exempted from the logging gate for the same reason DatadogLogger
   * exempts it: those runs are served from localhost, so the gate would deny them, and
   * this is the only path to DatadogLogger.init() — without the exemption RUM session
   * linking with CI Visibility would never initialize.
   *
   * @example Used for gating calls.
   */
  static isEnabled(): boolean {
    return !isStorybook() && (isLoggingEnabled() || DatadogLogger.isCypressCITest());
  }

  static log(options: Pick<LoggerOptions, 'error' | 'message' | 'messageContext'>) {
    const { error, message, messageContext } = options;
    console.log(message, options);

    if (!LoggingService.isEnabled()) {
      return;
    }
    DatadogLogger.log({ error, message, messageContext, status: 'info' });
  }

  static setUser(user?: User): void {
    if (!user || !LoggingService.isEnabled()) {
      return;
    }

    DatadogLogger.setUser({
      displayName: user.displayName,
      email: user.email,
      group: user.group,
      id: user.id,
      scope: user.scope,
    });
  }

  static trackAction(action: string, context?: Record<string, any>): void {
    if (!LoggingService.isEnabled()) {
      return;
    }

    DatadogLogger.trackAction(action, context);
    trackEvent({ event: action, eventInfo: context });
  }

  static trackPageView(path: string): void {
    if (!LoggingService.isEnabled()) {
      return;
    }

    DatadogLogger.trackPageView(path);
  }

  static warn(options: Pick<LoggerOptions, 'message' | 'messageContext'>) {
    const { message, messageContext } = options;
    console.warn(message, options);

    if (!LoggingService.isEnabled()) {
      return;
    }
    DatadogLogger.log({ message, messageContext, status: 'warn' });
  }
}

export default LoggingService;
